import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout, DetailBox } from '@/shared/ui/DetailLayout';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import {
  EMPTY_LEVEL,
  fetchLoyaltyLevel,
  saveLoyaltyLevel,
  type LevelValues,
  type LoyaltyLevelItem,
} from './api';

const toValues = (item: LoyaltyLevelItem): LevelValues => ({
  title: item.title ?? '',
  minPaymentsAmount: String(item.min_payments_amount ?? ''),
  iconColor: item.icon_color ?? '',
  color: item.color ?? '',
  textColor: item.text_color ?? '',
  accentColor: item.accent_color ?? '',
  cardBackground: item.card_background ?? '',
  cardTextColor: item.card_text_color ?? '',
});

type FieldConfig = { name: keyof LevelValues; label: string; errorKey: string; placeholder: string };

const MAIN_FIELDS: FieldConfig[] = [
  { name: 'title', label: 'Название уровня лояльности *', errorKey: 'title', placeholder: 'Введите название' },
  {
    name: 'minPaymentsAmount',
    label: 'Минимальный объем онлайн оплат *',
    errorKey: 'min_payments_amount',
    placeholder: 'Введите минимальный объем оплат',
  },
  { name: 'iconColor', label: 'Цвет иконки', errorKey: 'icon_color', placeholder: 'Укажите цвет иконки' },
];

const PANEL_FIELDS: FieldConfig[] = [
  { name: 'color', label: 'Цвет фона панели', errorKey: 'color', placeholder: 'Укажите цвет фона' },
  { name: 'textColor', label: 'Цвет текста на панели', errorKey: 'text_color', placeholder: 'Укажите цвет текста' },
  { name: 'accentColor', label: 'Фон акцентов на панели', errorKey: 'accent_color', placeholder: 'Укажите цвет акцента' },
];

const CARD_FIELDS: FieldConfig[] = [
  {
    name: 'cardBackground',
    label: 'Фон карточки',
    errorKey: 'card_background',
    placeholder: 'Укажите цвет фона карточки',
  },
  {
    name: 'cardTextColor',
    label: 'Цвет текста на карточке',
    errorKey: 'card_text_color',
    placeholder: 'Укажите цвет текста на карточке',
  },
];

const LevelForm = ({ id, initial }: { id?: number; initial: LevelValues }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const form = useEntityForm(
    initial,
    (values) => saveLoyaltyLevel(id, values),
    (res) => {
      void qc.invalidateQueries({ queryKey: ['loyalty-levels'] });
      if (!id && res.data?.id) navigate(`/loyalty/levels/edit/${res.data.id}`, { replace: true });
    },
  );

  const renderFields = (fields: FieldConfig[]) =>
    fields.map(({ name, label, errorKey, placeholder }) => (
      <TextField
        key={name}
        label={label}
        placeholder={placeholder}
        value={form.values[name]}
        errors={form.errors[errorKey]}
        onChange={(value) => form.setField(name, value, errorKey)}
      />
    ));

  return (
    <>
      {renderFields(MAIN_FIELDS)}
      <DetailBox title="Панель в дашборде">{renderFields(PANEL_FIELDS)}</DetailBox>
      <DetailBox title="Карточка в слайдере">{renderFields(CARD_FIELDS)}</DetailBox>
      <SaveBar isSaving={form.isSaving} saved={form.saved} errors={form.errors} onSave={form.save} />
    </>
  );
};

export const LevelCreatePage = () => (
  <DetailLayout title="Создание уровня лояльности">
    <LevelForm initial={EMPTY_LEVEL} />
  </DetailLayout>
);

export const LevelEditPage = () => {
  const id = useRouteId();
  const { data, isLoading, error } = useQuery({
    queryKey: ['loyalty-level', id],
    queryFn: () => fetchLoyaltyLevel(id),
  });

  return (
    <DetailLayout title="Редактирование уровня лояльности" isLoading={isLoading} error={error}>
      {data && <LevelForm key={id} id={id} initial={toValues(data)} />}
    </DetailLayout>
  );
};
